import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { api, useAuth, type Role } from "./auth";
import { AdminConsole } from "./AdminConsole";
import { ChatWorkspace } from "./ChatWorkspace";
import { CrmWorkspace } from "./CrmWorkspace";
import { OperationsWorkspace } from "./OperationsWorkspace";
import { PortalWorkspace } from "./PortalWorkspace";
import { WorkflowWorkspace } from "./WorkflowWorkspace";
import { WorkspaceErrorBoundary } from "./WorkspaceErrorBoundary";
import { KnowledgeWorkspace } from "./CommunityWorkspace";
import { UserProfile } from "./UserProfile";
import { ForcedPasswordChange } from "./ForcedPasswordChange";
import { UserAvatar } from "./UserAvatar";
import { PublicBusinessSite } from "./PublicBusinessSite";
import {
  ExpenseCommand,
  OfficeCommand,
  OptionsCommand,
  PeopleCommand,
  PlatformFeeCommand,
  ReminderQueue,
  RoleDashboard,
  ServiceCommand,
} from "./EnterpriseWorkspace";
import { WorkingListWorkspace } from "./WorkingListWorkspace";
import { LeadCommand } from "./LeadWorkspace";
import { TaskCommand } from "./TaskWorkspace";
import { CommissionWorkspace } from "./CommissionWorkspace";
import {
  Activity,
  Bell,
  BookOpen,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CarFront,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  ClipboardCheck,
  LayoutDashboard,
  LogOut,
  Menu,
  FileText,
  MessagesSquare,
  Mic,
  MoreHorizontal,
  Paperclip,
  Plus,
  Search,
  Send,
  Settings,
  ShieldCheck,
  Smile,
  Sparkles,
  UserCircle,
  Users,
  Wrench,
  X,
  Megaphone,
} from "lucide-react";

type Portal = "admin" | "staff" | "customer" | "owner" | "field";
type DashboardConfig = {
  title: string;
  eyebrow: string;
  description: string;
  metrics: Array<[string, string, string]>;
  tasks: string[];
  columns: string[];
  rows: string[][];
};

const configs: Record<Portal, DashboardConfig> = {
  admin: {
    title: "Group command centre",
    eyebrow: "Superadmin · All companies",
    description: "Revenue, risk and operations across every Realco business.",
    metrics: [
      ["AED 12.84M", "Gross sales", "+14.2%"],
      ["AED 438K", "Open receivables", "18 overdue"],
      ["92.4%", "Lead SLA", "+3.1 pts"],
      ["37", "Approvals waiting", "8 urgent"],
    ],
    tasks: [
      "Approve 6 tenancy contracts",
      "Review Royal Car licence gate",
      "Post August Workwise platform charges",
      "Resolve 4 portal sync failures",
    ],
    columns: ["Business", "Sales", "Pipeline", "Outstanding", "Status"],
    rows: [
      ["RealCO Capital", "AED 6.42M", "AED 18.7M", "AED 164K", "On track"],
      ["Central Royal", "AED 2.18M", "AED 4.3M", "AED 91K", "Attention"],
      ["Royal Rentacar", "AED 1.96M", "AED 3.1M", "AED 78K", "Licence gate"],
      ["Real Clean", "AED 1.14M", "AED 1.8M", "AED 42K", "On track"],
      ["I Maintain", "AED 1.14M", "AED 2.0M", "AED 63K", "On track"],
    ],
  },
  staff: {
    title: "Today’s sales workspace",
    eyebrow: "Agent portal · Dubai team",
    description:
      "Work the next-best action without losing sight of your pipeline.",
    metrics: [
      ["24", "Active leads", "7 hot"],
      ["7", "Tasks due today", "2 overdue"],
      ["AED 8.2M", "Open pipeline", "11 deals"],
      ["18 min", "Response time", "Target 120m"],
    ],
    tasks: [
      "Call Amira — Downtown 2BR",
      "Confirm Marina viewing at 16:30",
      "Upload Form A for RC-1048",
      "Follow up mortgage pre-approval",
    ],
    columns: ["Lead", "Requirement", "Budget", "Stage", "Next action"],
    rows: [
      ["Amira Khalid", "Downtown · 2 BR", "AED 2.4M", "Qualified", "Call now"],
      [
        "Naveen Shah",
        "Dubai Hills · Villa",
        "AED 6.8M",
        "Viewing",
        "Today 16:30",
      ],
      [
        "Daniel Reed",
        "Business Bay · Office",
        "AED 180K",
        "Negotiation",
        "Offer expires",
      ],
      ["Sara Khan", "JVC · 1 BR", "AED 950K", "New", "Due in 34m"],
    ],
  },
  customer: {
    title: "Your Realco account",
    eyebrow: "Customer portal",
    description:
      "Properties, bookings, services and payments in one secure place.",
    metrics: [
      ["3", "Active services", "All healthy"],
      ["AED 600", "Next payment", "Due 01 Oct"],
      ["2", "Open requests", "1 scheduled"],
      ["8", "Documents", "All verified"],
    ],
    tasks: [
      "Sign office handover form",
      "Review internet service order",
      "Confirm cleaning schedule",
      "Upload authorised employee list",
    ],
    columns: ["Service", "Reference", "Next date", "Amount", "Status"],
    rows: [
      [
        "Central Royal office",
        "Office 145",
        "01 Oct 2026",
        "AED 6,667",
        "Active",
      ],
      ["Business internet", "CR-INT-0145", "01 Oct 2026", "AED 600", "Active"],
      ["Office cleaning", "RC-CLN-8821", "07 Sep 2026", "AED 420", "Scheduled"],
      ["Maintenance", "WO-10483", "06 Sep 2026", "—", "Assigned"],
    ],
  },
  owner: {
    title: "Portfolio performance",
    eyebrow: "Owner & partner portal",
    description: "Occupancy, cash flow, approvals and asset performance.",
    metrics: [
      ["92%", "Occupancy", "+4 pts"],
      ["AED 284K", "Net income YTD", "+11.8%"],
      ["4", "Approvals waiting", "AED 18.2K"],
      ["1.8 days", "Response SLA", "Within target"],
    ],
    tasks: [
      "Approve AC quotation — Unit 1204",
      "Review September owner statement",
      "Renew management agreement",
      "Confirm asking price for Unit 604",
    ],
    columns: ["Asset", "Occupancy", "Income YTD", "Open items", "Performance"],
    rows: [
      ["Marina Gate 1204", "Occupied", "AED 86K", "1 approval", "Strong"],
      ["Creek Rise 604", "Vacant", "AED 41K", "Listing review", "Attention"],
      ["Central Royal 145", "Occupied", "AED 96K", "None", "Strong"],
      ["Royal Maison 1807", "74% nights", "AED 61K", "2 tasks", "On track"],
    ],
  },
  field: {
    title: "Field operations",
    eyebrow: "Driver & service portal",
    description: "A clear route from assignment to verified completion.",
    metrics: [
      ["6", "Jobs today", "2 completed"],
      ["94%", "On-time rate", "This month"],
      ["42 km", "Route remaining", "4 stops"],
      ["1", "Issue raised", "Awaiting review"],
    ],
    tasks: [
      "Vehicle checkout — Lamborghini Urus",
      "AC inspection — Office 145",
      "Deep clean — Marina Gate 1204",
      "Collect access card — Central Royal",
    ],
    columns: ["Time", "Assignment", "Location", "Customer", "Status"],
    rows: [
      ["09:00", "Vehicle delivery", "Palm Jumeirah", "D. Reed", "Completed"],
      ["11:30", "AC inspection", "Al Manara", "ABC Trading", "In progress"],
      ["14:00", "Deep cleaning", "Dubai Marina", "S. Khan", "Confirmed"],
      ["17:15", "Vehicle return", "DIFC", "A. Khalid", "Upcoming"],
    ],
  },
};

const nav: Array<[string, string, typeof LayoutDashboard]> = [
  ["Overview", "/dashboard", LayoutDashboard],
  ["Administration", "/admin?view=users", Users],
  ["Teams", "/admin?view=teams", Users],
  ["My working list", "/working-list", CheckCircle2],
  ["Tasks & reminders", "/modules/workflow", ClipboardCheck],
  ["Sales pipeline", "/modules/crm", Users],
  ["Properties", "/modules/real-estate", Building2],
  ["Team chat", "/chat", MessagesSquare],
  ["Community intelligence", "/knowledge", BookOpen],
  ["Central Royal", "/modules/business-center", BriefcaseBusiness],
  ["Fleet", "/modules/cars", CarFront],
  ["Cleaning", "/modules/cleaning", Sparkles],
  ["Maintenance", "/modules/maintenance", Wrench],
  ["Holiday homes", "/modules/holiday-homes", Building2],
  ["People & Compliance", "/people", UserCircle],
  ["Inventory management", "/office", BriefcaseBusiness],
  ["Expenses & P&L", "/expenses", CircleDollarSign],
  ["Deal commissions", "/commissions", CircleDollarSign],
  ["Platform invoices", "/platform-fees", FileText],
  ["Service catalogue", "/services", Settings],
  ["Dropdowns", "/admin-options", Settings],
  ["Finance", "/modules/finance", CircleDollarSign],
  ["Reports", "/modules/reports", Activity],
];

const permissionForPath: Record<string, string> = {
  "/people": "HR_MANAGE",
  "/office": "OFFICE_MANAGE",
  "/expenses": "EXPENSE_CREATE",
  "/commissions": "COMMISSIONS_VIEW",
  "/platform-fees": "FINANCE_VIEW",
  "/services": "SERVICES_MANAGE",
  "/admin-options": "ADMIN_MANAGE",
  "/knowledge": "COMMUNITY_EDIT",
  "/modules/crm": "LEADS_VIEW",
  "/modules/real-estate": "PROPERTIES_MANAGE",
  "/modules/cars": "FLEET_MANAGE",
  "/modules/cleaning": "SERVICES_MANAGE",
  "/modules/maintenance": "SERVICES_MANAGE",
  "/modules/finance": "FINANCE_VIEW",
  "/modules/reports": "REPORTS_VIEW",
};
const baselineForRole: Partial<Record<Role, string[]>> = {
  TEAM_LEAD: [
    "/dashboard",
    "/working-list",
    "/modules/workflow",
    "/modules/crm",
    "/modules/real-estate",
    "/knowledge",
    "/chat",
    "/profile",
  ],
  AGENT: [
    "/dashboard",
    "/working-list",
    "/modules/workflow",
    "/modules/crm",
    "/modules/real-estate",
    "/knowledge",
    "/chat",
    "/profile",
  ],
  STAFF: [
    "/dashboard",
    "/working-list",
    "/modules/workflow",
    "/chat",
    "/profile",
  ],
  HR: [
    "/dashboard",
    "/working-list",
    "/modules/workflow",
    "/people",
    "/chat",
    "/profile",
  ],
  OFFICE_MANAGER: [
    "/dashboard",
    "/working-list",
    "/modules/workflow",
    "/office",
    "/modules/business-center",
    "/chat",
    "/profile",
  ],
  FINANCE: [
    "/dashboard",
    "/working-list",
    "/modules/workflow",
    "/expenses",
    "/platform-fees",
    "/commissions",
    "/modules/finance",
    "/modules/reports",
    "/chat",
    "/profile",
  ],
};

function Modal({
  open,
  onClose,
  title,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
}) {
  if (!open) return null;
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.currentTarget === e.target) onClose();
      }}
    >
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <header>
          <div>
            <span className="kicker">Quick create</span>
            <h2 id="modal-title">{title}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </header>
        <div className="form-grid">
          <label>
            Full name
            <input autoFocus placeholder="Enter name" />
          </label>
          <label>
            Business
            <select>
              <option>RealCO Capital</option>
              <option>Central Royal</option>
              <option>Royal Rentacar</option>
              <option>Real Clean</option>
              <option>I Maintain</option>
            </select>
          </label>
          <label>
            Mobile
            <input placeholder="+971" />
          </label>
          <label>
            Email
            <input type="email" placeholder="name@example.com" />
          </label>
          <label className="span-2">
            Requirement
            <textarea
              rows={4}
              placeholder="Add the essential context, budget, dates and next action"
            />
          </label>
        </div>
        <footer>
          <button className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary" onClick={onClose}>
            Create record
          </button>
        </footer>
      </section>
    </div>
  );
}

function AppShell({ portal }: { portal: Portal }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [navOpen, setNavOpen] = useState(false);
  const [query, setQuery] = useState("");
  const c = configs[portal];
  const role = user?.role || "STAFF";
  const management = [
    "SUPERADMIN",
    "GROUP_ADMIN",
    "COMPANY_ADMIN",
    "COO",
    "OWNER",
  ].includes(role);
  const operator = [
    "TEAM_LEAD",
    "AGENT",
    "STAFF",
    "HR",
    "OFFICE_MANAGER",
    "FINANCE",
  ].includes(role);
  const profileEntry: [string, string, typeof LayoutDashboard] = [
    "My profile",
    "/profile",
    UserCircle,
  ];
  const visibleNav = management
    ? [
        ...(role === "OWNER"
          ? [
              ["Owner portfolio", "/owner", LayoutDashboard] as [
                string,
                string,
                typeof LayoutDashboard,
              ],
            ]
          : []),
        ...nav,
        profileEntry,
      ]
    : operator
      ? [
          ["My overview", "/dashboard", LayoutDashboard] as [
            string,
            string,
            typeof LayoutDashboard,
          ],
          ...nav.filter(
            ([, path]) =>
              (path !== "/dashboard" &&
                (baselineForRole[role] ?? []).includes(path)) ||
              (Boolean(permissionForPath[path]) &&
                Boolean(user?.permissions?.includes(permissionForPath[path]))),
          ),
          profileEntry,
        ].filter(
          (entry, index, all) =>
            all.findIndex((candidate) => candidate[1] === entry[1]) === index,
        )
      : [
          [
            role === "FIELD" ? "My assignments" : "My account",
            role === "FIELD" ? "/field" : "/customer",
            LayoutDashboard,
          ] as [string, string, typeof LayoutDashboard],
          ...(role === "CUSTOMER"
            ? [
                ["Customer chat", "/chat", MessagesSquare] as [
                  string,
                  string,
                  typeof LayoutDashboard,
                ],
              ]
            : []),
          profileEntry,
        ];
  const filtered = useMemo(
    () =>
      c.rows.filter((row) =>
        row.join(" ").toLowerCase().includes(query.toLowerCase()),
      ),
    [c.rows, query],
  );
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, location.search]);
  return (
    <div className="app-shell">
      <aside className={navOpen ? "sidebar open" : "sidebar"}>
        <div className="brand">
          <img
            className="brand-wordmark"
            src="/realco-wordmark.png"
            alt="Realco"
          />
          <div>
            <small>Group OS</small>
          </div>
          <button className="mobile-close" onClick={() => setNavOpen(false)}>
            <X />
          </button>
        </div>
        <nav>
          {visibleNav.map(([label, path, Icon]) => {
            const [pathname, search = ""] = path.split("?");
            const active =
              location.pathname === pathname &&
              (search
                ? location.search === `?${search}`
                : pathname === "/admin"
                  ? !location.search
                  : true);
            return (
              <Link className={active ? "active" : ""} to={path} key={label}>
                <Icon size={18} />
                <span>{label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div className="security-note">
            <ShieldCheck size={18} />
            <div>
              <strong>Protected workspace</strong>
              <span>Role access enforced</span>
            </div>
          </div>
          <button
            className="sidebar-signout"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
          >
            <LogOut size={17} /> Sign out
          </button>
        </div>
      </aside>
      <main>
        <header className="topbar">
          <button
            className="menu-button"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
          >
            <Menu />
          </button>
          <div className="global-search">
            <Search size={18} />
            <input
              aria-label="Search"
              placeholder="Search people, properties, invoices, vehicles…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <kbd>⌘ K</kbd>
          </div>
          {management && (
            <button
              className="button primary topbar-create"
              onClick={() => navigate("/admin?view=overview")}
            >
              <Plus size={16} />
              Create
            </button>
          )}
          <NotificationBell />
          <button className="profile" onClick={() => navigate("/profile")}>
            {user && (
              <UserAvatar
                userId={user.id}
                name={user.displayName}
                available={user.avatarAvailable}
                className="topbar-avatar"
              />
            )}
            <div>
              <strong>{user?.displayName}</strong>
              <small>{user?.role.replaceAll("_", " ")}</small>
            </div>
            <ChevronDown size={15} />
          </button>
        </header>
        <AnnouncementTicker />
        <WorkspaceErrorBoundary
          resetKey={`${location.pathname}${location.search}`}
        >
          {location.pathname === "/chat" ? (
            <ChatWorkspace embedded />
          ) : location.pathname === "/admin" ? (
            <AdminConsole />
          ) : location.pathname === "/dashboard" ? (
            <RoleDashboard />
          ) : location.pathname === "/working-list" ? (
            <WorkingListWorkspace />
          ) : location.pathname === "/people" ? (
            <PeopleCommand />
          ) : location.pathname === "/expenses" ? (
            <ExpenseCommand />
          ) : location.pathname === "/commissions" ? (
            <CommissionWorkspace />
          ) : location.pathname === "/platform-fees" ? (
            <PlatformFeeCommand />
          ) : location.pathname === "/services" ? (
            <ServiceCommand />
          ) : location.pathname === "/admin-options" ? (
            <OptionsCommand />
          ) : location.pathname === "/office" ? (
            <OfficeCommand />
          ) : location.pathname === "/knowledge" ? (
            <KnowledgeWorkspace />
          ) : location.pathname === "/profile" ? (
            <UserProfile />
          ) : location.pathname.startsWith("/modules/") ? (
            <ModuleWorkspace
              moduleKey={location.pathname.split("/").pop() || "operations"}
            />
          ) : (
            <PortalWorkspace portal={portal === "admin" ? "staff" : portal} />
          )}
        </WorkspaceErrorBoundary>
        <ReminderQueue />
        <footer className="app-footer">
          Powered by <strong>Workwise</strong> · Copyright © 2026
        </footer>
      </main>
    </div>
  );
}

function AnnouncementTicker() {
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [index, setIndex] = useState(0);
  useEffect(() => {
    let active = true;
    const load = () =>
      api<{ data: Array<Record<string, unknown>> }>(
        "/api/v1/enterprise/announcements",
      )
        .then((result) => {
          if (active) setRows(result.data);
        })
        .catch(() => undefined);
    void load();
    const refresh = window.setInterval(load, 60000);
    return () => {
      active = false;
      window.clearInterval(refresh);
    };
  }, []);
  useEffect(() => {
    if (rows.length < 2) return;
    const timer = window.setInterval(
      () => setIndex((value) => (value + 1) % rows.length),
      8000,
    );
    return () => window.clearInterval(timer);
  }, [rows.length]);
  if (!rows.length) return null;
  const item = rows[index % rows.length];
  return (
    <aside
      className={`announcement-ticker ${String(item.severity || "INFO").toLowerCase()}`}
      role="status"
    >
      <Megaphone size={17} />
      <strong>{String(item.title)}</strong>
      <span>{String(item.body)}</span>
      {Boolean(item.cta_url)&&<a href={String(item.cta_url)}>{String(item.cta_label||"Open")}</a>}
      {rows.length > 1 && (
        <small>
          {(index % rows.length) + 1}/{rows.length}
        </small>
      )}
    </aside>
  );
}

function NotificationBell() {
  const { csrfToken } = useAuth();
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [open, setOpen] = useState(false);
  async function load() {
    try {
      const result = await api<{ data: Array<Record<string, unknown>> }>(
        "/api/v1/notifications",
      );
      setRows(result.data);
    } catch {}
  }
  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 60000);
    return () => window.clearInterval(timer);
  }, []);
  const unread = rows.filter((x) => !x.read_at).length;
  async function readAll() {
    if (!csrfToken) return;
    await api("/api/v1/notifications/read", csrfToken, {
      method: "POST",
      body: JSON.stringify({ all: true }),
    });
    await load();
  }
  return (
    <div className="notification-anchor">
      <button
        className="icon-button badge"
        aria-label="Notifications"
        onClick={() => setOpen((value) => !value)}
      >
        <Bell size={19} />
        {unread > 0 && <i>{unread > 99 ? "99+" : unread}</i>}
      </button>
      {open && (
        <div className="notification-popover">
          <header>
            <div>
              <strong>Notifications</strong>
              <small>{unread} unread</small>
            </div>
            {unread > 0 && (
              <button onClick={() => void readAll()}>Mark all read</button>
            )}
          </header>
          <div>
            {rows.slice(0, 8).map((row) => (
              <article
                className={!row.read_at ? "unread" : ""}
                key={String(row.id)}
              >
                <strong>{String(row.title)}</strong>
                <p>{String(row.body || row.category)}</p>
                <small>
                  {new Date(String(row.created_at)).toLocaleString("en-AE")}
                </small>
              </article>
            ))}
            {!rows.length && <div className="empty">No notifications.</div>}
          </div>
          <Link to="/modules/workflow" onClick={() => setOpen(false)}>
            Open control centre
          </Link>
        </div>
      )}
    </div>
  );
}

function ModuleWorkspace({ moduleKey }: { moduleKey: string }) {
  if (moduleKey === "crm") return <LeadCommand />;
  if (moduleKey === "workflow") return <TaskCommand />;
  return <OperationsWorkspace moduleKey={moduleKey} />;
}

function Login() {
  const { user, login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("os@workw.com");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (user) return <Navigate to={homeFor(user.role)} replace />;
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (challenge) {
        await verifyMfa(challenge, code, remember);
        navigate("/dashboard");
        return;
      }
      const result = await login(email, password, remember);
      if (result.status === "mfa_required") {
        setChallenge(result.challenge);
      } else if (result.status === "password_change_required")
        navigate("/change-password");
      else navigate("/dashboard");
    } catch (value) {
      setError(value instanceof Error ? value.message : "Sign in failed");
    } finally {
      setBusy(false);
    }
  }
  async function requestPasswordReset() {
    const target = window.prompt(
      "Enter the email address for your Realco account:",
      email,
    );
    if (!target) return;
    setBusy(true);
    setError("");
    try {
      const result = await api<{ message: string }>(
        "/api/v1/auth/forgot-password",
        null,
        { method: "POST", body: JSON.stringify({ email: target }) },
      );
      setError(result.message);
    } catch (value) {
      setError(
        value instanceof Error
          ? value.message
          : "Password reset could not be requested",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="login-page">
      <div className="login-art">
        <div className="login-copy">
          <img
            className="hero-wordmark"
            src="/realco-wordmark.png"
            alt="Realco"
          />
          <span className="kicker light">One operating platform</span>
          <h1>
            Every business.
            <br />
            One clear next action.
          </h1>
          <p>
            Securely manage property, tenants, vehicles, services and finance
            across the Realco group.
          </p>
          <div className="login-stat">
            <strong>7</strong>
            <span>
              UAE emirates
              <br />
              ready from launch
            </span>
          </div>
        </div>
      </div>
      <form className="login-card" onSubmit={submit}>
        <div className="login-brand">
          <img
            className="login-wordmark"
            src="/realco-wordmark.png"
            alt="Realco"
          />
        </div>
        <div>
          <span className="kicker">Secure sign in</span>
          <h2>{challenge ? "Verify your identity" : "Welcome back"}</h2>
          <p>
            {challenge
              ? "Enter the six-digit authenticator code or a recovery code."
              : "Use your Realco account. Workwise sign-in appears when enabled by the superadmin."}
          </p>
        </div>
        {error && (
          <div className="form-error" role="alert">
            {error}
          </div>
        )}
        {challenge ? (
          <>
            <label>
              Authentication or recovery code
              <input
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={20}
                placeholder="000000"
              />
            </label>
            <button
              className="button primary full"
              disabled={busy || code.length < 6}
            >
              {busy ? "Verifying…" : "Verify and continue"}
            </button>
            <button
              type="button"
              className="text-button centered"
              onClick={() => {
                setChallenge("");
                setCode("");
                setError("");
              }}
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <label>
              Email address
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </label>
            <div className="login-options">
              <label className="check">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                Remember this device
              </label>
              <button
                type="button"
                className="text-button"
                onClick={() => void requestPasswordReset()}
              >
                Forgot password?
              </button>
            </div>
            <button
              className="button primary full"
              disabled={busy || password.length < 8}
            >
              {busy ? "Signing in…" : "Sign in securely"}
            </button>
            <button type="button" className="button workwise" disabled>
              <img src="/workwise-logo.png" alt="Workwise" />
              Continue with Workwise <small>Integration pending</small>
            </button>
          </>
        )}
        <footer>
          Powered by <strong>Workwise</strong> · Copyright © 2026
        </footer>
      </form>
    </div>
  );
}

function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const valid =
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /\d/.test(password) &&
    /[^A-Za-z0-9]/.test(password) &&
    password === confirmation;
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    setMessage("");
    try {
      await api("/api/v1/auth/reset-password", null, {
        method: "POST",
        body: JSON.stringify({ token, password, confirmation }),
      });
      setMessage("Password updated. Redirecting to sign in…");
      window.setTimeout(() => navigate("/login", { replace: true }), 1200);
    } catch (value) {
      setMessage(
        value instanceof Error ? value.message : "Password reset failed",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="reset-page">
      <form className="login-card reset-card" onSubmit={submit}>
        <img
          className="login-wordmark"
          src="/realco-wordmark.png"
          alt="Realco"
        />
        <div>
          <span className="kicker">Secure account recovery</span>
          <h2>Set a new password</h2>
          <p>The link is single-use and expires after 30 minutes.</p>
        </div>
        {!token && (
          <div className="form-error">
            This reset link is incomplete. Request a new link from the sign-in
            page.
          </div>
        )}
        {message && (
          <div className="form-error" role="status">
            {message}
          </div>
        )}
        <label>
          New password
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>
        <label>
          Repeat new password
          <input
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            required
          />
        </label>
        <div className="password-guidelines">
          <span className={password.length >= 8 ? "met" : ""}>
            8+ characters
          </span>
          <span className={/[A-Z]/.test(password) ? "met" : ""}>Uppercase</span>
          <span className={/[a-z]/.test(password) ? "met" : ""}>Lowercase</span>
          <span className={/\d/.test(password) ? "met" : ""}>Number</span>
          <span className={/[^A-Za-z0-9]/.test(password) ? "met" : ""}>
            Symbol
          </span>
          <span
            className={
              password !== "" && password === confirmation ? "met" : ""
            }
          >
            Passwords match
          </span>
        </div>
        <button
          className="button primary full"
          disabled={!token || !valid || busy}
        >
          {busy ? "Updating…" : "Update password"}
        </button>
        <button
          type="button"
          className="text-button centered"
          onClick={() => navigate("/login")}
        >
          Back to sign in
        </button>
        <footer>
          Powered by <strong>Workwise</strong> · Copyright © 2026
        </footer>
      </form>
    </div>
  );
}

function homeFor(role: Role) {
  if (role === "CUSTOMER") return "/customer";
  if (role === "FIELD") return "/field";
  return "/dashboard";
}
function Protected({
  roles,
  children,
  allowPasswordChange = false,
}: {
  roles: Role[];
  children: ReactNode;
  allowPasswordChange?: boolean;
}) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="auth-loading">
        <img src="/realco-wordmark.png" alt="Realco" />
        <span>Opening secure workspace…</span>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.mustChangePassword && !allowPasswordChange)
    return <Navigate to="/change-password" replace />;
  if (!roles.includes(user.role))
    return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function PublicSurface({ kind }: { kind: string }) {
  return <PublicBusinessSite kind={kind} />;
}

export function App() {
  const all: Role[] = [
    "SUPERADMIN",
    "GROUP_ADMIN",
    "COMPANY_ADMIN",
    "COO",
    "OWNER",
    "TEAM_LEAD",
    "AGENT",
    "STAFF",
    "HR",
    "OFFICE_MANAGER",
    "FINANCE",
    "CUSTOMER",
    "FIELD",
  ];
  const admins: Role[] = [
    "SUPERADMIN",
    "GROUP_ADMIN",
    "COMPANY_ADMIN",
    "COO",
    "OWNER",
  ];
  const staff: Role[] = [
    "SUPERADMIN",
    "GROUP_ADMIN",
    "COMPANY_ADMIN",
    "COO",
    "OWNER",
    "TEAM_LEAD",
    "AGENT",
    "STAFF",
    "HR",
    "OFFICE_MANAGER",
    "FINANCE",
  ];
  useEffect(() => {
    document.title = "Workwise - Realco";
  }, []);
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/change-password"
        element={
          <Protected roles={all} allowPasswordChange>
            <ForcedPasswordChange />
          </Protected>
        }
      />
      <Route
        path="/profile"
        element={
          <Protected roles={all}>
            <AppShell portal="admin" />
          </Protected>
        }
      />
      <Route
        path="/dashboard"
        element={
          <Protected roles={staff}>
            <AppShell portal="staff" />
          </Protected>
        }
      />
      <Route
        path="/working-list"
        element={
          <Protected roles={staff}>
            <AppShell portal="staff" />
          </Protected>
        }
      />
      <Route
        path="/admin"
        element={
          <Protected roles={admins}>
            <AppShell portal="admin" />
          </Protected>
        }
      />
      <Route
        path="/people"
        element={
          <Protected
            roles={[
              "SUPERADMIN",
              "GROUP_ADMIN",
              "COMPANY_ADMIN",
              "COO",
              "OWNER",
              "HR",
            ]}
          >
            <AppShell portal="admin" />
          </Protected>
        }
      />
      <Route
        path="/expenses"
        element={
          <Protected roles={staff}>
            <AppShell portal="admin" />
          </Protected>
        }
      />
      <Route
        path="/commissions"
        element={
          <Protected roles={staff}>
            <AppShell portal="admin" />
          </Protected>
        }
      />
      <Route
        path="/platform-fees"
        element={
          <Protected
            roles={["SUPERADMIN", "GROUP_ADMIN", "COO", "OWNER", "FINANCE"]}
          >
            <AppShell portal="admin" />
          </Protected>
        }
      />
      <Route
        path="/services"
        element={
          <Protected roles={staff}>
            <AppShell portal="admin" />
          </Protected>
        }
      />
      <Route
        path="/admin-options"
        element={
          <Protected roles={admins}>
            <AppShell portal="admin" />
          </Protected>
        }
      />
      <Route
        path="/office"
        element={
          <Protected
            roles={[
              "SUPERADMIN",
              "GROUP_ADMIN",
              "COMPANY_ADMIN",
              "COO",
              "OWNER",
              "OFFICE_MANAGER",
            ]}
          >
            <AppShell portal="admin" />
          </Protected>
        }
      />
      <Route
        path="/knowledge"
        element={
          <Protected roles={staff}>
            <AppShell portal="admin" />
          </Protected>
        }
      />
      <Route
        path="/modules/:module"
        element={
          <Protected roles={staff}>
            <AppShell portal="admin" />
          </Protected>
        }
      />
      <Route
        path="/staff"
        element={
          <Protected roles={staff}>
            <AppShell portal="staff" />
          </Protected>
        }
      />
      <Route
        path="/customer"
        element={
          <Protected roles={["CUSTOMER", "SUPERADMIN"]}>
            <AppShell portal="customer" />
          </Protected>
        }
      />
      <Route
        path="/owner"
        element={
          <Protected roles={["OWNER", "SUPERADMIN"]}>
            <AppShell portal="owner" />
          </Protected>
        }
      />
      <Route
        path="/field"
        element={
          <Protected roles={["FIELD", "SUPERADMIN"]}>
            <AppShell portal="field" />
          </Protected>
        }
      />
      <Route
        path="/chat"
        element={
          <Protected roles={all}>
            <AppShell portal="admin" />
          </Protected>
        }
      />
      {[
        "real-estate",
        "cars",
        "cleaning",
        "maintenance",
        "business-center",
        "holiday-homes",
      ].map((k) => (
        <Route key={k} path={`/${k}`} element={<PublicSurface kind={k} />} />
      ))}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}
